import { apiClient } from './client'
import type { PageResponse } from '@/types/common'

export interface AdminUser {
  id: string
  email: string
  displayName: string | null
  role: 'USER' | 'ADMIN'
  tier: 'FREE' | 'PRO' | 'ADMIN'
  premiumOverride: boolean | null
  createdAt: string
}

export interface ProductMetric {
  key: string
  label: string
  value: number
  cohortSize: number | null
  unit: string
  description: string
  category: string
}

export interface MetricSection {
  category: string
  title: string
  metrics: ProductMetric[]
}

export interface AdminMetricsResponse {
  periodDays: number
  sections: MetricSection[]
  metrics: ProductMetric[]
}

export interface WordKnowledgeResponse {
  wordId: string
  word: string
  confidenceScore: number | null
  confidenceSource: string | null
  activeExplanationVersions: number
  activeLocalizationVersions: number
}

export interface AdminFeedbackItem {
  id: string
  userId: string | null
  email: string | null
  category: string
  message: string
  pagePath: string | null
  createdAt: string
  read: boolean
  readAt: string | null
  readByUserId: string | null
  readByEmail: string | null
}

export type FeedbackInboxStatus = 'ALL' | 'UNREAD' | 'READ'

export const adminApi = {
  listUsers: (params: { search?: string; page?: number; size?: number }) =>
    apiClient
      .get<PageResponse<AdminUser>>('/admin/users', { params })
      .then((r) => r.data),

  updateUserRole: (userId: string, role: 'USER' | 'ADMIN') =>
    apiClient.patch<AdminUser>(`/admin/users/${userId}/role`, { role }).then((r) => r.data),

  updateUserPremium: (userId: string, enabled: boolean) =>
    apiClient.patch<AdminUser>(`/admin/users/${userId}/premium`, { enabled }).then((r) => r.data),

  getMetrics: (period = 7) =>
    apiClient.get<AdminMetricsResponse>('/admin/metrics', { params: { period } }).then((r) => r.data),

  reEnrichWord: (wordId: string) =>
    apiClient.post<WordKnowledgeResponse>(`/admin/words/${wordId}/re-enrich`).then((r) => r.data),

  listFeedback: (params: { status?: FeedbackInboxStatus; page?: number; size?: number } = {}) =>
    apiClient
      .get<PageResponse<AdminFeedbackItem>>('/admin/feedback', { params })
      .then((r) => r.data),

  feedbackUnreadCount: () =>
    apiClient.get<{ count: number }>('/admin/feedback/unread-count').then((r) => r.data.count),

  updateFeedbackRead: (id: string, read: boolean) =>
    apiClient
      .patch<AdminFeedbackItem>(`/admin/feedback/${id}/read`, { read })
      .then((r) => r.data),
}
